import { isClerkAPIResponseError } from "@clerk/react/errors";

/**
 * Short machine-readable reason behind a failed Clerk custom-flow call.
 * For logs and pending-session recovery — not for the form UI.
 */
export const clerkErrorDetail = (error: unknown): string | null => {
  if (isClerkAPIResponseError(error)) {
    const codes = (error.errors ?? [])
      .map((apiError) => apiError.code)
      .filter(Boolean);
    return codes.length ? codes.join(", ") : `clerk_${error.status}`;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return null;
};

export const withAuthDetail = (
  message: string,
  detail?: string | null,
): string => (detail ? `${message} (${detail})` : message);

const USER_FACING_CODES: Record<string, string> = {
  form_code_incorrect: "auth.errorCodeIncorrect",
  form_code_expired: "auth.errorCodeExpired",
  form_param_format_invalid: "auth.errorInvalidEmail",
};

export const clerkApiCodes = (error: unknown): string[] => {
  if (isClerkAPIResponseError(error)) {
    return (error.errors ?? []).map((apiError) => apiError.code).filter(Boolean);
  }
  if (error && typeof error === "object" && "errors" in error) {
    const nested = (error as { errors?: { code?: string }[] }).errors;
    return (nested ?? [])
      .map((apiError) => apiError.code)
      .filter((code): code is string => !!code);
  }
  return [];
};

/** True for an exact Clerk code or a param-suffixed variant (`_email_address`). */
export const hasClerkCode = (error: unknown, prefix: string): boolean =>
  clerkApiCodes(error).some(
    (code) => code === prefix || code.startsWith(`${prefix}_`),
  );

const userFacingKeyForCode = (code: string): string | undefined =>
  USER_FACING_CODES[code];

/**
 * Translated auth form copy. Known Clerk user errors map to specific strings;
 * everything else is generic. Machine codes stay in the console, not the UI.
 */
export const authErrorMessage = (
  translate: (key: string) => string,
  error: unknown,
): string => {
  const codes = clerkApiCodes(error);
  if (codes.length === 0) {
    const detail = clerkErrorDetail(error);
    if (detail) {
      codes.push(...detail.split(",").map((code) => code.trim()));
    }
  }
  for (const code of codes) {
    const key = userFacingKeyForCode(code);
    if (key) return translate(key);
  }
  return translate("auth.errorGeneric");
};
